import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { FacebookIcon, InstagramIcon, YoutubeIcon, MailIcon, PhoneIcon, MapPinIcon, ArrowRightIcon, XIcon, EyeIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { XLogoIcon } from './shared/Icons';
// ── Global Visitor Counter (shared across all visitors via counterapi.dev) ──
function VisitorStats() {
  const {
    language
  } = useLanguage();
  const isEs = language === 'ES';
  const [visitorCount, setVisitorCount] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const SESSION_KEY = 'haa_global_counted';
    const LOCAL_KEY = 'haa_global_count_cache';
    const alreadyCounted = sessionStorage.getItem(SESSION_KEY);
    // If already counted this session, just read the count (don't increment again)
    const endpoint = alreadyCounted ? 'https://api.counterapi.dev/v1/haa-honduran-association/site-visits' : 'https://api.counterapi.dev/v1/haa-honduran-association/site-visits/up';
    fetch(endpoint).then(res => res.json()).then(data => {
      const count = data?.count ?? 0;
      setVisitorCount(count);
      localStorage.setItem(LOCAL_KEY, String(count));
      if (!alreadyCounted) {
        sessionStorage.setItem(SESSION_KEY, 'true');
      }
    }).catch(() => {
      // Fallback: show cached count from localStorage
      const cached = localStorage.getItem(LOCAL_KEY);
      setVisitorCount(cached ? parseInt(cached, 10) : 0);
    }).finally(() => setLoading(false));
  }, []);
  return <div className="flex items-center justify-center" data-id="element-1900">
      <div className="flex items-center gap-2.5 bg-white/5 border border-white/10 rounded-xl px-4 py-2.5" data-id="element-1901">
        <div className="w-7 h-7 rounded-lg bg-haa-blue/30 flex items-center justify-center" data-id="element-1902">
          <EyeIcon className="w-3.5 h-3.5 text-haa-skyblue" data-id="element-1903" />
        </div>
        <div data-id="element-1904">
          <p className="font-nunito text-[10px] uppercase tracking-wider text-white/30 leading-none mb-0.5" data-id="element-1905">
            {isEs ? 'Visitas Totales' : 'Total Visits'}
          </p>
          {loading ? <div className="w-12 h-4 bg-haa-skyblue/20 rounded animate-pulse" data-id="element-1906" /> : <p className="font-bebas text-lg text-haa-skyblue tracking-wider leading-none" data-id="element-1907">
              {visitorCount.toLocaleString()}
            </p>}
        </div>
      </div>
    </div>;
}
export function Footer() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-50px'
  });
  const {
    t,
    language
  } = useLanguage();
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [accessibilityOpen, setAccessibilityOpen] = useState(false);
  const quickLinks = [{
    labelKey: 'footer.link.home',
    href: '#home'
  }, {
    labelKey: 'footer.link.mission',
    href: '#mission'
  }, {
    labelKey: 'footer.link.community',
    href: '#community'
  }, {
    labelKey: 'footer.link.hondurasFacts',
    href: '#community'
  }, {
    labelKey: 'footer.link.news',
    href: '#news'
  }, {
    labelKey: 'footer.link.leadership',
    href: '#leadership'
  }, {
    labelKey: 'footer.link.consular',
    href: '#consular-affairs'
  }, {
    labelKey: 'footer.link.exploreHonduras',
    href: '#explore-honduras'
  }, {
    labelKey: 'footer.link.businessInvestments',
    href: '#business-investments'
  }, {
    labelKey: 'footer.link.upliftingVoices',
    href: '#social-voices'
  }, {
    labelKey: 'footer.link.nonprofitDirectory',
    href: '#nonprofit-directory'
  }, {
    labelKey: 'footer.link.faq',
    href: '#faq'
  }];
  return <footer id="contact" className="bg-haa-navy" aria-label="Site footer" data-id="element-1908">
      {/* Smooth gradient into footer */}
      <div className="h-16 bg-gradient-to-b from-gray-50 to-haa-navy" data-id="element-1909" />

      <div ref={ref} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-8" data-id="element-1910">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-10 mb-12" data-id="element-1911">
          {/* Column 1: About + Contact */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={inView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6
        }} data-id="element-1912">
            <div className="flex items-center gap-3 mb-5" data-id="element-1913">
              <img src="https://cdn.magicpatterns.com/uploads/b1GB8Z9aEvoE58p9BdX4qx/HonduranAmericanLogoNew.png" alt="Honduran American Association" className="h-14 w-auto object-contain brightness-0 invert" data-id="element-1914" />
            </div>

            <div className="flex items-center gap-3 mb-6" data-id="element-1915">
              {[{
              Icon: FacebookIcon,
              gradient: 'from-haa-blue to-blue-900',
              label: 'Facebook',
              href: '#'
            }, {
              Icon: InstagramIcon,
              gradient: 'from-haa-midblue to-haa-skyblue',
              label: 'Instagram',
              href: 'https://www.instagram.com/honduranamerican/'
            }, {
              Icon: XLogoIcon,
              gradient: 'from-haa-skyblue to-blue-400',
              label: 'X',
              href: 'https://x.com'
            }, {
              Icon: YoutubeIcon,
              gradient: 'from-haa-blue to-haa-navy',
              label: 'YouTube',
              href: '#'
            }].map(({
              Icon,
              gradient,
              label,
              href
            }) => <motion.a key={label} href={href} target={href !== '#' ? '_blank' : undefined} rel={href !== '#' ? 'noopener noreferrer' : undefined} aria-label={label} whileHover={{
              scale: 1.2,
              boxShadow: '0 0 15px rgba(74, 144, 217, 0.4)'
            }} whileTap={{
              scale: 0.9
            }} className={`w-9 h-9 rounded-full bg-gradient-to-br ${gradient} flex items-center justify-center shadow-lg`} data-id="element-1916">
                  <Icon className="w-4 h-4 text-white" data-id="element-1917" />
                </motion.a>)}
            </div>

            {/* Contact Us - below social icons */}
            <h3 className="font-bebas text-haa-skyblue text-2xl tracking-widest mb-4" data-id="element-1918">
              {t('footer.contact')}
            </h3>
            <ul className="space-y-3" data-id="element-1919">
              <li className="flex items-start gap-3" data-id="element-1920">
                <MapPinIcon className="w-4 h-4 text-haa-skyblue flex-shrink-0 mt-0.5" data-id="element-1921" />
                <span className="font-nunito text-white/60 text-sm" data-id="element-1922">
                  Washington D.C.
                </span>
              </li>
              <li className="flex items-center gap-3" data-id="element-1923">
                <PhoneIcon className="w-4 h-4 text-haa-skyblue flex-shrink-0" data-id="element-1924" />
                <a href="tel:+17033131465" className="font-nunito text-white/60 hover:text-white text-sm transition-colors" data-id="element-1925">
                  +1-703-313-1465
                </a>
              </li>
              <li className="flex items-center gap-3" data-id="element-1926">
                <svg className="w-4 h-4 text-haa-skyblue flex-shrink-0" viewBox="0 0 24 24" fill="currentColor" data-id="element-1927">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" data-id="element-1928" />
                </svg>
                <a href="https://wa.me/17033131465" target="_blank" rel="noopener noreferrer" className="font-nunito text-white/60 hover:text-white text-sm transition-colors" data-id="element-1929">
                  {language === 'ES' ? 'Enviar mensaje por WhatsApp' : 'Message us on WhatsApp'}
                </a>
              </li>
              <li className="flex items-center gap-3" data-id="element-1930">
                <MailIcon className="w-4 h-4 text-haa-skyblue flex-shrink-0" data-id="element-1931" />
                <a href="mailto:info@honduranamerican.org" className="font-nunito text-white/60 hover:text-white text-sm transition-colors" data-id="element-1932">
                  info@honduranamerican.org
                </a>
              </li>
            </ul>
          </motion.div>

          {/* Column 2: Quick Links */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={inView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.1
        }} data-id="element-1933">
            <h3 className="font-bebas text-haa-skyblue text-2xl tracking-widest mb-5" data-id="element-1934">
              {t('footer.quickLinks')}
            </h3>
            <ul className="space-y-2.5" data-id="element-1935">
              {quickLinks.map(link => <li key={link.labelKey} data-id="element-1936">
                  <a href={link.href} className="font-nunito text-white/60 hover:text-haa-skyblue text-sm flex items-center gap-2 group transition-colors" data-id="element-1937">
                    <ArrowRightIcon className="w-3.5 h-3.5 text-haa-blue group-hover:text-haa-skyblue transition-colors flex-shrink-0" data-id="element-1938" />
                    {t(link.labelKey)}
                  </a>
                </li>)}
            </ul>
          </motion.div>
        </div>

        {/* Divider */}
        <div className="h-px w-full bg-gradient-to-r from-transparent via-haa-skyblue/20 to-transparent mb-8" data-id="element-1939" />

        {/* Visitor Stats */}
        <VisitorStats data-id="element-1940" />

        {/* Divider */}
        <div className="h-px w-full bg-gradient-to-r from-transparent via-haa-skyblue/20 to-transparent mb-8 mt-8" data-id="element-1941" />

        {/* Bottom Bar */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-1942">
          <p className="font-nunito text-white/40 text-sm text-center sm:text-left" data-id="element-1943">
            {t('footer.copyright')}
          </p>
          <div className="flex items-center gap-4" data-id="element-1944">
            <button onClick={() => setPrivacyOpen(true)} className="font-nunito text-white/40 hover:text-white/70 text-sm transition-colors cursor-pointer bg-transparent border-none" data-id="element-1945">
              {t('footer.privacy')}
            </button>
            <span className="text-white/20" data-id="element-1946">·</span>
            <button onClick={() => setAccessibilityOpen(true)} className="font-nunito text-white/40 hover:text-white/70 text-sm transition-colors cursor-pointer bg-transparent border-none" data-id="element-1947">
              {t('footer.accessibility')}
            </button>
          </div>
        </div>
      </div>

      {/* Privacy Policy Modal */}
      <AnimatePresence data-id="element-1948">
        {privacyOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setPrivacyOpen(false)} data-id="element-1949">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.3,
          ease: [0.16, 1, 0.3, 1]
        }} className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-id="element-1950">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100" data-id="element-1951">
                <div className="flex items-center gap-3" data-id="element-1952">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-haa-blue to-haa-navy flex items-center justify-center shadow-sm" data-id="element-1953">
                    <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1954">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" data-id="element-1955" />
                    </svg>
                  </div>
                  <h2 className="font-bebas text-3xl text-haa-navy tracking-wider" data-id="element-1956">
                    {language === 'ES' ? 'Política de Privacidad' : 'Privacy Policy'}
                  </h2>
                </div>
                <button onClick={() => setPrivacyOpen(false)} className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600" aria-label="Close" data-id="element-1957">
                  <XIcon className="w-5 h-5" data-id="element-1958" />
                </button>
              </div>

              {/* Content */}
              <div className="overflow-y-auto px-6 py-6 space-y-4" data-id="element-1959">
                {language === 'ES' ? <>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-1960">
                      La Asociación Hondureño-Americana respeta su privacidad y
                      se compromete a proteger cualquier información personal
                      que usted elija compartir con nosotros a través de este
                      sitio web.
                    </p>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-1961">
                      Podemos recopilar información básica como su nombre,
                      dirección de correo electrónico, número de teléfono y
                      cualquier mensaje o detalle que envíe a través de
                      formularios de contacto, suscripciones a boletines,
                      consultas de voluntariado, formularios de interés en
                      donaciones u otras funciones del sitio web. También
                      podemos recopilar información técnica limitada, como el
                      tipo de navegador, información del dispositivo y datos
                      generales de uso del sitio web, para ayudarnos a mejorar
                      el sitio y la experiencia del usuario.
                    </p>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-1962">
                      Utilizamos esta información para responder a consultas,
                      compartir actualizaciones sobre nuestro trabajo, mejorar
                      nuestros programas y sitio web, y comunicarnos con
                      simpatizantes, socios y miembros de la comunidad. No
                      vendemos ni alquilamos su información personal a terceros.
                    </p>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-1963">
                      Podemos utilizar servicios de terceros de confianza para
                      ayudar a operar el sitio web, procesar formularios, enviar
                      correos electrónicos o analizar el tráfico del sitio web.
                      Esos proveedores solo pueden acceder a la información
                      necesaria para realizar esos servicios.
                    </p>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-1964">
                      Nuestro sitio web puede contener enlaces a sitios web de
                      terceros. No somos responsables de las prácticas de
                      privacidad o el contenido de esos sitios externos.
                    </p>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-1965">
                      Tomamos medidas razonables para proteger su información,
                      pero ningún método de transmisión o almacenamiento en
                      línea es completamente seguro. Al usar este sitio web,
                      usted comprende y acepta este riesgo.
                    </p>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-1966">
                      Si desea actualizar, corregir o solicitar la eliminación
                      de su información, contáctenos a través de la página de
                      contacto del sitio web o el correo electrónico oficial de
                      la organización.
                    </p>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-1967">
                      Podemos actualizar esta Política de Privacidad de vez en
                      cuando. Cualquier cambio se publicará en esta página con
                      la fecha de vigencia actualizada.
                    </p>
                    <div className="mt-2 p-4 bg-blue-50 rounded-xl border border-blue-100" data-id="element-1968">
                      <p className="font-nunito text-gray-700 text-sm leading-relaxed" data-id="element-1969">
                        Si tiene alguna pregunta o inquietud sobre esta Política
                        de Privacidad, contáctenos en{' '}
                        <a href="mailto:info@honduranamerican.org" className="text-haa-blue hover:text-haa-navy font-semibold underline transition-colors" data-id="element-1970">
                          info@honduranamerican.org
                        </a>
                      </p>
                    </div>
                  </> : <>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-1971">
                      The Honduran-American Association respects your privacy
                      and is committed to protecting any personal information
                      you choose to share with us through this website.
                    </p>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-1972">
                      We may collect basic information such as your name, email
                      address, phone number, and any message or details you
                      submit through contact forms, newsletter sign-ups,
                      volunteer inquiries, donation interest forms, or other
                      website features. We may also collect limited technical
                      information, such as browser type, device information, and
                      general website usage data, to help us improve the site
                      and user experience.
                    </p>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-1973">
                      We use this information to respond to inquiries, share
                      updates about our work, improve our programs and website,
                      and communicate with supporters, partners, and community
                      members. We do not sell or rent your personal information
                      to third parties.
                    </p>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-1974">
                      We may use trusted third-party services to help operate
                      the website, process forms, send emails, or analyze
                      website traffic. Those providers may only access
                      information necessary to perform those services.
                    </p>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-1975">
                      Our website may contain links to third-party websites. We
                      are not responsible for the privacy practices or content
                      of those external sites.
                    </p>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-1976">
                      We take reasonable steps to protect your information, but
                      no method of online transmission or storage is completely
                      secure. By using this website, you understand and accept
                      this risk.
                    </p>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-1977">
                      If you would like to update, correct, or request removal
                      of your information, please contact us through the
                      website's contact page or official organizational email.
                    </p>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-1978">
                      We may update this Privacy Policy from time to time. Any
                      changes will be posted on this page with the updated
                      effective date.
                    </p>
                    <div className="mt-2 p-4 bg-blue-50 rounded-xl border border-blue-100" data-id="element-1979">
                      <p className="font-nunito text-gray-700 text-sm leading-relaxed" data-id="element-1980">
                        If you have any questions or concerns about this Privacy
                        Policy, please contact us at{' '}
                        <a href="mailto:info@honduranamerican.org" className="text-haa-blue hover:text-haa-navy font-semibold underline transition-colors" data-id="element-1981">
                          info@honduranamerican.org
                        </a>
                      </p>
                    </div>
                  </>}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/50" data-id="element-1982">
                <button onClick={() => setPrivacyOpen(false)} className="w-full py-2.5 bg-haa-navy hover:bg-haa-blue text-white font-nunito font-bold text-sm rounded-xl transition-colors" data-id="element-1983">
                  {language === 'ES' ? 'Cerrar' : 'Close'}
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Accessibility Statement Modal */}
      <AnimatePresence data-id="element-1984">
        {accessibilityOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setAccessibilityOpen(false)} data-id="element-1985">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.3,
          ease: [0.16, 1, 0.3, 1]
        }} className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-id="element-1986">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100" data-id="element-1987">
                <div className="flex items-center gap-3" data-id="element-1988">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-haa-blue to-haa-navy flex items-center justify-center shadow-sm" data-id="element-1989">
                    <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1990">
                      <circle cx="12" cy="4.5" r="2.5" data-id="element-1991" />
                      <path d="M12 7v5" data-id="element-1992" />
                      <path d="M8 22l4-10 4 10" data-id="element-1993" />
                      <path d="M6 12h12" data-id="element-1994" />
                    </svg>
                  </div>
                  <h2 className="font-bebas text-3xl text-haa-navy tracking-wider" data-id="element-1995">
                    {language === 'ES' ? 'Declaración de Accesibilidad' : 'Accessibility Statement'}
                  </h2>
                </div>
                <button onClick={() => setAccessibilityOpen(false)} className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600" aria-label="Close" data-id="element-1996">
                  <XIcon className="w-5 h-5" data-id="element-1997" />
                </button>
              </div>

              {/* Content */}
              <div className="overflow-y-auto px-6 py-6 space-y-4" data-id="element-1998">
                {language === 'ES' ? <>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-1999">
                      La Asociación Hondureño-Americana se compromete a hacer
                      que este sitio web sea accesible y utilizable para la
                      mayor cantidad de personas posible, incluidas las personas
                      con discapacidades. Creemos que todos deberían poder
                      acceder a la información sobre nuestra misión, programas y
                      recursos con dignidad y facilidad.
                    </p>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-2000">
                      Trabajamos continuamente para mejorar la accesibilidad y
                      usabilidad de este sitio y seguir las mejores prácticas
                      reconocidas para la accesibilidad digital. Como parte de
                      ese compromiso, nos esforzamos por diseñar contenido que
                      sea claro, legible y accesible en todos los dispositivos y
                      tecnologías de asistencia.
                    </p>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-2001">
                      A pesar de nuestros esfuerzos, algunas áreas del sitio web
                      pueden no ser completamente accesibles o pueden estar aún
                      en proceso de mejora. Agradecemos los comentarios de los
                      visitantes que encuentren barreras al usar nuestro sitio.
                    </p>
                    <div className="mt-2 p-4 bg-blue-50 rounded-xl border border-blue-100" data-id="element-2002">
                      <p className="font-nunito text-gray-700 text-sm leading-relaxed" data-id="element-2003">
                        Si tiene dificultades para acceder a cualquier parte de
                        este sitio web, nota un problema de accesibilidad o
                        desea solicitar asistencia, contáctenos en{' '}
                        <a href="mailto:info@honduranamerican.org" className="text-haa-blue hover:text-haa-navy font-semibold underline transition-colors" data-id="element-2004">
                          info@honduranamerican.org
                        </a>
                        . Haremos todo lo posible para responder con prontitud y
                        brindarle la información o el apoyo que necesite.
                      </p>
                    </div>
                  </> : <>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-2005">
                      The Honduran-American Association is committed to making
                      this website accessible and usable for as many people as
                      possible, including people with disabilities. We believe
                      everyone should be able to access information about our
                      mission, programs, and resources with dignity and ease.
                    </p>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-2006">
                      We are continually working to improve the accessibility
                      and usability of this site and to follow recognized best
                      practices for digital accessibility. As part of that
                      commitment, we aim to design content that is clear,
                      readable, and accessible across devices and assistive
                      technologies.
                    </p>
                    <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-2007">
                      Despite our efforts, some areas of the website may not yet
                      be fully accessible or may still be in the process of
                      improvement. We welcome feedback from visitors who
                      encounter barriers while using our site.
                    </p>
                    <div className="mt-2 p-4 bg-blue-50 rounded-xl border border-blue-100" data-id="element-2008">
                      <p className="font-nunito text-gray-700 text-sm leading-relaxed" data-id="element-2009">
                        If you experience difficulty accessing any part of this
                        website, notice an accessibility issue, or would like to
                        request assistance, please contact us at{' '}
                        <a href="mailto:info@honduranamerican.org" className="text-haa-blue hover:text-haa-navy font-semibold underline transition-colors" data-id="element-2010">
                          info@honduranamerican.org
                        </a>
                        . We will do our best to respond promptly and provide
                        the information or support you need.
                      </p>
                    </div>
                  </>}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/50" data-id="element-2011">
                <button onClick={() => setAccessibilityOpen(false)} className="w-full py-2.5 bg-haa-navy hover:bg-haa-blue text-white font-nunito font-bold text-sm rounded-xl transition-colors" data-id="element-2012">
                  {language === 'ES' ? 'Cerrar' : 'Close'}
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </footer>;
}